<script setup lang="ts">
import { computed } from 'vue';
import Cost from './Cost.vue';

/**
 * Minimal structural type — accepts both `PlanData` (offering side) and
 * `Subscription['plan']` (subscription side) without committing to either.
 */
interface PriceablePlan {
  currency?: string;
  interval?: string;
  amount?: number | null;
}

const props = defineProps<{ plan: PriceablePlan }>();

// Placeholder for i18n
const formatPrice = (fallback: string, options?: any) => {
  // Simple placeholder
  let text = fallback;
  if (options?.total) {
    text = text.replace('{total}', options.total);
  }
  if (options?.currency) {
    text = text.replace('{currency}', options.currency.toUpperCase());
  }
  return text;
};

// Computed properties
const interval = computed(() => {
  // Ensure interval is one of the expected types
  const lowerInterval = props.plan.interval?.toLowerCase();
  if (lowerInterval === 'month' || lowerInterval === 'year' || lowerInterval === 'lifetime') {
    return lowerInterval;
  }
  return 'lifetime'; // Default or handle other cases
});

const intervalFallback = computed(
  () => (interval.value === 'month' ? '/ month' : `/ ${interval.value}`), // Fallback text
);

const formattedAnnualTotal = computed(() =>
  interval.value === 'year' ? formatPrice(props.plan.currency ?? 'usd', true) : '',
);

const annuallyFallback = computed(() => (interval.value === 'year' ? '{total} billed annually' : ''));
</script>

<template>
  <div class="price-display">
    <div class="price-display__primary">
      <Cost :currency="plan?.currency ?? 'usd'" :interval="interval" :amount="plan?.amount || 0" />
      <div v-if="plan.amount !== 0 && interval !== 'lifetime'" class="interval">
        {{ formatPrice(intervalFallback) }}
      </div>
    </div>
    <div v-if="plan.amount !== 0 && interval === 'year'" class="annually">
      {{ formatPrice(annuallyFallback, { total: formattedAnnualTotal }) }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.price-display {
  text-align: center;
}

.price-display__primary {
  display: inline-flex;
  align-items: flex-end;
  gap: 0.25rem;
}

.interval,
.annually {
  color: var(--text-color-lightest, #6b7280); // Fallback color
  font-size: var(--font-size-xs, 0.75rem); // Fallback size
  font-family: var(--font-family-ui, ui-sans-serif, system-ui, sans-serif); // Fallback font
  text-transform: uppercase;
  line-height: 1.2;
}

.annually {
  margin-top: 0.25rem;
}
</style>
